<script setup lang="ts">
const panelRef = ref(null);

const props = defineProps({
  width: {
    type: String as PropType<"default" | "medium" | "large" | "xl" | "4xl">,
    default: "default",
  },
  hideX: {
    type: Boolean,
    required: false,
    default: false,
  },
  desktopMaxWidthClass: {
    type: String,
    default: "",
  },
});

const extraClasses = computed(() => {
  const classes: string[] = [];
  switch (props.width) {
    case "default":
      classes.push("max-w-sm");
      break;
    case "medium":
      classes.push("max-w-md");
      break;
    case "large":
      classes.push("max-w-lg");
      break;
    case "xl":
      classes.push("max-w-2xl");
      break;
    case "4xl":
      classes.push("max-w-4xl");
      break;
    default:
      classes.push("max-w-sm");
  }
  if (props.desktopMaxWidthClass) {
    classes.push(props.desktopMaxWidthClass);
  }
  return classes;
});

defineEmits(["close"]);
</script>

<template>
  <HeadlessTransitionRoot as="template" :show="true">
    <HeadlessDialog
      :initial-focus="panelRef"
      as="div"
      class="relative z-30"
      @close="$emit('close')">
      <HeadlessTransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0">
        <div class="fixed inset-0 bg-black/70 transition-opacity" />
      </HeadlessTransitionChild>

      <div class="fixed inset-0 z-10 overflow-y-auto">
        <div
          class="flex min-h-full justify-center sm:p-4 text-center items-center">
          <HeadlessTransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <HeadlessDialogPanel
              ref="panelRef"
              tabindex="-1"
              class="relative transform w-full overflow-hidden sm:rounded-lg bg-neutral-800 border border-[#5B5B5B] pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full"
              :class="extraClasses">
              <div>
                <HeadlessDialogTitle
                  as="h7"
                  class="font-bold leading-6 text-neutral-200 text-2xl border-b-[#5B5B5B] border-b mb-4 flex items-center py-4 px-4 sm:px-6">
                  <slot name="header" />

                  <NuxtIcon
                    v-if="!hideX"
                    name="mdi:close"
                    :alt="$t('main.imageAltTexts.closeIcon')"
                    class="w-9 h-9 ml-auto text-neutral-300 cursor-pointer"
                    @click="$emit('close')" />
                </HeadlessDialogTitle>
                <div class="px-4 sm:px-6">
                  <slot name="content" />
                </div>
              </div>
            </HeadlessDialogPanel>
          </HeadlessTransitionChild>
        </div>
      </div>
    </HeadlessDialog>
  </HeadlessTransitionRoot>
</template>
